'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import Cookies from 'js-cookie'
import { useAuth } from '../contexts/AuthContext'
import {
  hasTrackingConsent,
  onConsentChanged,
  readConsent,
} from '../lib/consent'
import {
  decodeLandingToken,
  LANDING_COOKIES,
  LANDING_TOKEN_PARAM,
  landingPageMatches,
} from '../lib/landing-experiment'
import {
  clearLandingClicks,
  flushLandingClicks,
  flushLandingWithdrawals,
  getLandingExperimentToken,
  queueLandingWithdrawal,
  recordLandingClick,
  sendLandingEvent,
} from '../lib/landing-experiment-client'
import {
  landingClickTarget,
  LANDING_CLICK_SECTIONS,
  normalizeLandingBrowserLocale,
} from '../lib/landing-clicks'

export default function LandingExperimentTracker() {
  const pathname = usePathname()
  const { i18n } = useTranslation()
  const { user, authResolved } = useAuth()

  useEffect(() => {
    let disposed = false
    let inFlight = false
    let retryTimer
    let withdrawalTimer
    let withdrawalInFlight = false
    let withdrawalAttempts = 0
    let attempts = 0
    let clickTimer
    let lastClickAt = 0
    let exposureTimer
    let scanTimer
    let sectionObserver
    let observedElements = new WeakSet()
    let stateToken
    let exposed = false
    let localeRecorded = false
    let acknowledgedSections = new Set()
    let visibleSections = new Set()
    const renderedPage = (assignment) => {
      const rendered = document.querySelector('[data-landing-variant]')
      return window.location.pathname === '/' &&
        landingPageMatches(assignment, rendered?.dataset)
        ? rendered
        : null
    }
    const loadExposure = (token, assignment) => {
      if (stateToken === token) return
      stateToken = token
      let saved
      try {
        saved = JSON.parse(
          sessionStorage.getItem(
            `pc_landing_exposure:${assignment.enrollment_id}:v2`,
          ) || 'null',
        )
      } catch {
        /* optional storage */
      }
      exposed = saved?.exposed === true
      localeRecorded = saved?.localeRecorded === true
      acknowledgedSections = new Set(
        (Array.isArray(saved?.sections) ? saved.sections : []).filter(
          (section) => LANDING_CLICK_SECTIONS.includes(section),
        ),
      )
      visibleSections = new Set(acknowledgedSections)
    }
    const saveExposure = (assignment) => {
      try {
        sessionStorage.setItem(
          `pc_landing_exposure:${assignment.enrollment_id}:v2`,
          JSON.stringify({
            exposed,
            localeRecorded,
            sections: [...acknowledgedSections],
          }),
        )
      } catch {
        /* backend deduplicates */
      }
    }
    const scanSections = () => {
      if (disposed || user || !hasTrackingConsent()) return
      const token = getLandingExperimentToken()
      const assignment = decodeLandingToken(token)
      const rendered = renderedPage(assignment)
      if (!rendered) return
      loadExposure(token, assignment)
      if (typeof IntersectionObserver === 'undefined') return
      if (!sectionObserver)
        sectionObserver = new IntersectionObserver(
          (entries) => {
            let changed = false
            for (const entry of entries) {
              const section =
                entry.target.dataset.landingSection ||
                (entry.target.tagName === 'HEADER' ? 'header' : 'footer')
              // Tall galleries can never be 50% visible on a phone. Require half
              // of smaller sections, or a meaningful third of the viewport.
              const minimumHeight = Math.min(
                entry.boundingClientRect.height * 0.5,
                window.innerHeight * 0.35,
              )
              if (
                entry.isIntersecting &&
                entry.intersectionRect.height >= minimumHeight &&
                entry.intersectionRect.width >=
                  entry.boundingClientRect.width * 0.5 &&
                !visibleSections.has(section)
              ) {
                visibleSections.add(section)
                sectionObserver.unobserve(entry.target)
                changed = true
              }
            }
            if (changed) {
              clearTimeout(exposureTimer)
              exposureTimer = setTimeout(update, 750)
            }
          },
          { threshold: [0, 0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 1] },
        )
      const candidates = [
        ...rendered.querySelectorAll('[data-landing-section]'),
        ...[...document.querySelectorAll('header,footer')].filter(
          (element) => !element.closest('[data-landing-variant]'),
        ),
      ]
      for (const element of candidates) {
        const section =
          element.dataset.landingSection ||
          (element.tagName === 'HEADER' ? 'header' : 'footer')
        if (
          !LANDING_CLICK_SECTIONS.includes(section) ||
          observedElements.has(element)
        )
          continue
        observedElements.add(element)
        if (!visibleSections.has(section)) sectionObserver.observe(element)
      }
    }
    const recordClick = (event) => {
      if (
        !event.isTrusted ||
        user ||
        window.location.pathname !== '/' ||
        (event.type === 'auxclick' && event.button !== 1) ||
        Date.now() - lastClickAt < 200
      )
        return
      const token = getLandingExperimentToken()
      const assignment = decodeLandingToken(token)
      if (!renderedPage(assignment)) return
      const target = landingClickTarget(event.target, window.location.origin)
      if (!target || !recordLandingClick(token, target)) return
      lastClickAt = Date.now()
      clearTimeout(clickTimer)
      // A real link may leave this document immediately. keepalive handles that
      // without blocking navigation; local feature interactions are batched.
      if (
        event.target.closest?.('a[href]') ||
        target.action.startsWith('gallery_card')
      )
        flushLandingClicks(token)
      else clickTimer = setTimeout(() => flushLandingClicks(token), 500)
    }
    const flushClicks = () => {
      flushLandingClicks()
    }
    const withdraw = async () => {
      if (disposed || withdrawalInFlight) return
      withdrawalInFlight = true
      let success = await flushLandingWithdrawals()
      // A returning account can be on a different browser from acquisition.
      // Its explicit refusal must also stop account-level payment attribution.
      if (readConsent()?.analyticsConsent === false && authResolved && user) {
        const key = `pc_landing_account_withdrawn:${user.uid}`
        let sent = false
        try {
          sent = sessionStorage.getItem(key) === '1'
        } catch {
          /* backend deduplicates */
        }
        if (!sent) {
          try {
            const accepted = await sendLandingEvent(
              'withdraw',
              null,
              await user.getIdToken(),
            )
            success = accepted && success
            if (accepted)
              try {
                sessionStorage.setItem(key, '1')
              } catch {
                /* optional optimization */
              }
          } catch {
            success = false
          }
        }
      }
      withdrawalInFlight = false
      if (!success && !disposed && withdrawalAttempts++ < 2)
        withdrawalTimer = setTimeout(withdraw, 3000 * withdrawalAttempts)
    }
    const update = async () => {
      if (disposed || inFlight) return
      const token = getLandingExperimentToken()
      if (!token) return
      const assignment = decodeLandingToken(token)
      if (!assignment) return
      inFlight = true
      let success = true
      try {
        // QA preview never enrolls. Ack the page shown, not a prefetch or a
        // signup route; the first-served denominator lives on the backend.
        const rendered = renderedPage(assignment)
        if (!user && rendered) {
          loadExposure(token, assignment)
          const sections = [...visibleSections].filter(
            (section) => !acknowledgedSections.has(section),
          )
          // B hydrates in English before its locale chunk loads. Ack the page
          // immediately, but do not freeze that temporary language as context.
          const localeReady =
            rendered.dataset.landingExperiment === 'baseline' ||
            (!i18n.isLanguageChangingTo &&
              rendered.dataset.landingLocale ===
                (i18n.resolvedLanguage || i18n.language || 'en'))
          if (!exposed || sections.length || (!localeRecorded && localeReady)) {
            const context = {
              ...(localeReady
                ? { locale: rendered.dataset.landingLocale || 'en' }
                : {}),
              browser_locale: normalizeLandingBrowserLocale(navigator.language),
              device:
                window.innerWidth < 768
                  ? 'mobile'
                  : window.innerWidth < 1024
                    ? 'tablet'
                    : 'desktop',
            }
            const accepted = await sendLandingEvent('exposed', token, null, {
              context,
              sections,
            })
            success = accepted && success
            if (accepted) {
              exposed = true
              if (localeReady) localeRecorded = true
              sections.forEach((section) => acknowledgedSections.add(section))
              saveExposure(assignment)
            }
          }
          scanSections()
        }
        if (authResolved && user) {
          const key = `pc_landing_resolved:${assignment.enrollment_id}:${user.uid}`
          let sent = false
          try {
            sent = sessionStorage.getItem(key) === '1'
          } catch {
            /* optional optimization */
          }
          if (!sent) {
            const accepted = await sendLandingEvent(
              'resolve',
              token,
              await user.getIdToken(),
            )
            success = accepted && success
            if (accepted)
              try {
                sessionStorage.setItem(key, '1')
              } catch {
                /* backend deduplicates */
              }
          }
        }
      } catch {
        success = false
      } finally {
        inFlight = false
      }
      if (!success && !disposed && attempts++ < 3)
        retryTimer = setTimeout(update, 2500 * 2 ** (attempts - 1))
      else if (success) attempts = 0
      // A section can become visible while its previous batch is in flight.
      if (
        success &&
        !disposed &&
        [...visibleSections].some(
          (section) => !acknowledgedSections.has(section),
        )
      ) {
        clearTimeout(exposureTimer)
        exposureTimer = setTimeout(update, 750)
      }
    }
    const consentChanged = () => {
      if (hasTrackingConsent()) {
        // The first consent-required visit has no assignment. Ask the server
        // for its first eligible rendering after an explicit acceptance.
        if (window.location.pathname === '/' && !getLandingExperimentToken()) {
          window.location.reload()
          return
        }
        update()
        flushClicks()
        return
      }
      clearLandingClicks()
      queueLandingWithdrawal([
        ...LANDING_COOKIES.map((name) => Cookies.get(name)),
        new URLSearchParams(window.location.search).get(LANDING_TOKEN_PARAM),
      ])
      LANDING_COOKIES.forEach((name) => Cookies.remove(name, { path: '/' }))
      sectionObserver?.disconnect()
      observedElements = new WeakSet()
      const url = new URL(window.location.href)
      if (url.searchParams.has(LANDING_TOKEN_PARAM)) {
        url.searchParams.delete(LANDING_TOKEN_PARAM)
        window.history.replaceState(window.history.state, '', url)
      }
      withdraw()
    }
    // Also clear a stale enrollment if consent was withdrawn in another tab.
    if (!hasTrackingConsent()) consentChanged()
    else {
      exposureTimer = setTimeout(() => {
        update()
        scanSections()
      }, 100)
      flushClicks()
    }
    withdraw()
    const unsubscribe = onConsentChanged(consentChanged)
    const online = () => {
      withdraw()
      update()
      flushClicks()
      scanSections()
    }
    const visible = () => {
      if (document.visibilityState === 'visible') online()
    }
    const pagehide = () => {
      update()
      flushClicks()
    }
    // This tracker is outside the auth/loading gate. Streamed or hydrated
    // landing markup can appear after its effect; absence is not an ack.
    const domObserver = new MutationObserver(() => {
      clearTimeout(scanTimer)
      scanTimer = setTimeout(() => {
        update()
        scanSections()
      }, 100)
    })
    if (window.location.pathname === '/' && !user)
      domObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'data-landing-variant',
          'data-landing-experiment',
          'data-landing-locale',
        ],
      })
    window.addEventListener('online', online)
    document.addEventListener('click', recordClick, true)
    document.addEventListener('auxclick', recordClick, true)
    window.addEventListener('pagehide', pagehide)
    document.addEventListener('visibilitychange', visible)
    return () => {
      disposed = true
      clearTimeout(retryTimer)
      clearTimeout(withdrawalTimer)
      clearTimeout(clickTimer)
      clearTimeout(exposureTimer)
      clearTimeout(scanTimer)
      sectionObserver?.disconnect()
      domObserver.disconnect()
      unsubscribe()
      window.removeEventListener('online', online)
      document.removeEventListener('click', recordClick, true)
      document.removeEventListener('auxclick', recordClick, true)
      window.removeEventListener('pagehide', pagehide)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [pathname, user, authResolved, i18n])
  return null
}
